import { useEffect, useState } from "react";
import { ReactComponent as FilterBy } from "assets/images/svgs/filterBy.svg";
import { FiSearch } from "react-icons/fi";
import FilterDrawer from "components/drawers/host-side/FilterDrawer";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import { EVENT_STATUS, EVENT_TIMEFRAMES, GENDERS, } from "utils/helpers/enums/shared.enum";
import { useDebounce } from "hooks/useDebounce";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import EventCard from "components/cards/event-card";
import { useSelector } from "react-redux";
import Pagination from "components/pagination";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import MinorBreadcrumb, { MinorBreadcrumbLink } from "components/breadcrumbs/MinorBreadcrumb";
import useStore from "hooks/useStore";
import useMinor from "../useHooks";
import { MinorEventsFilterDTO } from "utils/helpers/models/minor/minor-event-filter.dto";



const MinorExperiences = () => {
  const breadcrumbLinks: MinorBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.minorHome },
    {
      title: "Experiences",
      path: siteRoutes.minorExperiences,
    },
  ];
  const { getUserGender } = useStore();
  const userGender = getUserGender();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<MinorEventsFilterDTO>(() => {
    const defaultFilters = new MinorEventsFilterDTO();
    defaultFilters.timeFrame = EVENT_TIMEFRAMES.UPCOMING;
    return defaultFilters;
  });
  const { getEvents } = useMinor();
  const debounceSearch = useDebounce(filters.searchTitle, 1000);
  const [data, setData] = useState<EventDTO[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>("upcoming");
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const allFilterTabs = [
    {
      title: "Upcoming",
      value: EVENT_TIMEFRAMES.UPCOMING,
      type: "timeFrame",
      id: "upcoming",
    },
    {
      title: "Past",
      value: EVENT_TIMEFRAMES.PAST,
      type: "timeFrame",
      id: "past",
    },
    {
      title: "Wishlist",
      value: EVENT_STATUS.WISHLIST,
      type: "status",
      id: "wishlist",
    },
  ];

  const toggleDrawer = () => {
    setDrawerOpen(prev => !prev);
  };

  const getAllEvents = (filters: MinorEventsFilterDTO) => {
    const queryParams: any = {};
    for (let key in filters) {
      if (
        filters[key]?.toString() &&
        filters[key] !== EVENT_TIMEFRAMES.ALL &&
        filters[key] !== EVENT_STATUS.ALL &&
        filters[key] !== ""
      ) {
        queryParams[key] = filters[key];
      }
    }
    getEvents(setData, queryParams, [], setFilters);

    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };


  useEffect(() => {
    getAllEvents(filters);
  }, [debounceSearch]);

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;
    filters[name] = value;
    setFilters({ ...filters });
    if (name !== "searchTitle") {
      getAllEvents(filters);
    }
  };

  const handleTabChange = (
    filterName: string,
    value: string,
    tabId: string
  ) => {
    // Reset data immediately to show loading state
    setData([]);

    const newFilters = { ...filters };

    // Clear all drawer-related filters when tab changes
    newFilters.isSession = "";

    // Set only the selected filter
    newFilters[filterName] = value;

    // Only send timeframe if filterName is "timeFrame", otherwise delete it
    if (filterName === "timeFrame") {
      delete newFilters.status; // Remove status so only timeframe is sent
    } else if (filterName === "status") {
      delete newFilters.timeFrame; // Remove timeframe so only status is sent
    }

    // Reset pagination when changing tabs
    newFilters.page = 0;
    newFilters.size = 12;

    // Clear all applied filters when tab changes
    setAppliedFilters({});

    // Update selected tab
    setSelectedTab(tabId);

    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const onApplyDrawerFilters = (drawerFilters: any) => {
    const newFilters = { ...filters };
    const newAppliedFilters: any = {};

    for (let key in drawerFilters) {
      if (drawerFilters[key] && drawerFilters[key] !== "") {
        newFilters[key] = drawerFilters[key];
        newAppliedFilters[key] = drawerFilters[key];
      } else {
        delete newFilters[key];
        delete newAppliedFilters[key];
      }
    }

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const removeAppliedFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    const newAppliedFilters = { ...appliedFilters };

    delete newFilters[filterKey];
    delete newAppliedFilters[filterKey];

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  // Check if a tab is currently active
  const isTabActive = (tab: any) => {
    return selectedTab === tab.id;
  };


  const handlePageChange = (pageInfo: { selected: number, limit: number }) => {
    filters.page = pageInfo.selected;
    filters.size = pageInfo.limit;
    getAllEvents(filters);
  };


  return (
    <div className="container min-h-screen mb-10">
      <MinorBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl font-bold mb-4 mt-6">Experiences</h2>
      <div className="flex justify-between  flex-wrap items-center gap-5">
        <div className="flex flex-wrap gap-5 items-center">
          <div className="flex space-x-4 md:space-x-10 pb-2">
            {allFilterTabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(tab.type, tab.value, tab.id)}
                className={`text-base font-semibold pb-1 flex items-center ${
                  isTabActive(tab)
                    ? "text-minorPrimary border-b-2 border-minorPrimary"
                    : "text-grayText hover:text-minorPrimary"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="search"
              name="searchTitle"
              placeholder="Find Experiences"
              value={filters.searchTitle}
              onChange={handleFilterChange}
              className="w-full pl-10 pr-4 py-2 h-[51px] rounded-lg border border-inputsBorderColor outline-none"
            />
          </div>
          <button
            onClick={toggleDrawer}
            className="text-labelTextColor flex gap-1 font-medium"
          >
            Filter by <FilterBy />
          </button>

          {/* Applied Filters Display */}
          {Object.keys(appliedFilters).length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {Object.entries(appliedFilters).map(([key, value]) => {
                const getDisplayLabel = (key: string, value: any) => {
                  if (key === "isSession") {
                    return value === "true" ? "Sessions" : "Workshops";
                  }
                  if (key === "ageGroups") {
                    return `${value}`.replace(/_/g, " ");
                  }
                  return `${key}: ${value}`;
                };

                return (
                  <div
                    key={key}
                    className="flex items-center gap-1 bg-yellowText/10 text-yellowText px-3 py-1 rounded-full text-sm"
                  >
                    <span className="capitalize">
                      {getDisplayLabel(key, value)}
                    </span>
                    <button
                      onClick={() => removeAppliedFilter(key)}
                      className="text-red-500 hover:text-red-700 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        {data.map((event) => (
          <EventCard event={event} key={event.id} />
        ))}
      </div>

      <div className="flex items-center justify-center ">
        <CustomMessageDisplay
          show={!isLoading && !data.length && !isInitialLoad}
          title={
            selectedTab === "upcoming"
              ? "No Upcoming Experiences"
              : selectedTab === "past"
              ? "No Past Experiences"
              : selectedTab === "wishlist"
              ? "No Wishlist Experiences"
              : "No Experiences Found"
          }
          slogan={
            selectedTab === "upcoming"
              ? "You have no upcoming experiences scheduled. Browse and join new experiences!"
              : selectedTab === "past"
              ? "Your past experiences will appear here once they're completed"
              : selectedTab === "wishlist"
              ? "You haven't added any experiences to your wishlist yet. Start exploring!"
              : "No experiences available at the moment"
          }
          image={noFoundGif}
          imageAlt="No experiences found"
          className="py-12"
          imageClassName="w-24 h-24"
          titleClassName="text-2xl font-bold text-blackie"
          sloganClassName="text-gray-500 text-base"
        />
      </div>

      {data?.length > 0 && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={'#DE9C27'}
          activeTextColor={'#ffffff'}
        />
      )}
      {drawerOpen && (
        <FilterDrawer
          isOpen={drawerOpen}
          onClose={toggleDrawer}
          onApply={onApplyDrawerFilters}
          currentFilters={appliedFilters}
        />
      )}
    </div>
  );
};

export default MinorExperiences;

